import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { ApiTokensPage } from '../features/apitokens/ApiTokensPage';
import { AuditPage } from '../features/audit/AuditPage';
import { BackupPage } from '../features/backup/BackupPage';
import { AutomationPage } from '../features/automation/AutomationPage';
import { WebhooksPage } from '../features/automation/WebhooksPage';
import { SecurityPage } from '../features/auth/SecurityPage';
import { CustomerFilePage } from '../features/crm/CustomerFilePage';
import { CustomerListPage } from '../features/crm/CustomerListPage';
import { ImportPage } from '../features/crm/ImportPage';
import { CustomFieldsPage } from '../features/customfields/CustomFieldsPage';
import { HandbookPage } from '../features/handbook/HandbookPage';
import { InboundQueuePage } from '../features/mail/InboundQueuePage';
import { AnnouncementsPage } from '../features/mail/AnnouncementsPage';
import { MailAdminPage } from '../features/mail/MailAdminPage';
import { UsersPage } from '../features/users/UsersPage';
import { RolesPage } from '../features/roles/RolesPage';
import { ChecklistTemplatePage } from '../features/masterdata/ChecklistTemplatePage';
import { CategoryPage } from '../features/masterdata/CategoryPage';
import { MasterDataPage } from '../features/masterdata/MasterDataPage';
import { PriorityMatrixPage } from '../features/masterdata/PriorityMatrixPage';
import { KnowledgePage } from '../features/knowledge/KnowledgePage';
import { BrandingPage } from '../features/portal/BrandingPage';
import { PrivacyPage } from '../features/privacy/PrivacyPage';
import { MacrosPage } from '../features/shortcuts/MacrosPage';
import { DashboardPage } from '../features/reporting/DashboardPage';
import { ReportsPage } from '../features/reporting/ReportsPage';
import { TimeReportPage } from '../features/timetracking/TimeReportPage';
import { CalendarPage } from '../features/appointments/CalendarPage';
import { SlaAdminPage } from '../features/sla/SlaAdminPage';
import { SnippetsPage } from '../features/shortcuts/SnippetsPage';
import { TemplatesPage } from '../features/shortcuts/TemplatesPage';
import { BoardPage } from '../features/tickets/BoardPage';
import { WebFormsPage } from '../features/webform/WebFormsPage';
import { TicketDetailPage } from '../features/tickets/TicketDetailPage';
import { TicketListPage } from '../features/tickets/TicketListPage';
import { TokenManagementPage } from '../features/tokens/TokenManagementPage';
import { isSortValue } from '../features/tickets/columns';
import { STATE_CATEGORIES } from '../features/tickets/presentation';
import type { StateCategory } from '../features/tickets/api';
import { StatusPage } from '../features/system/StatusPage';
import { AppLayout } from './AppLayout';
import { AuthGate } from './AuthGate';

/**
 * Die Routen.
 *
 * **Zwei Anwendungen, ein Codebestand** (docs/02, Abschnitt 4): Die
 * Agentenoberflaeche liegt unter `/app`, das Kundenportal unter `/portal`.
 * Getrennte Router und Layouts, gemeinsame Bausteine.
 *
 * Das Portal wird ueber `lazyRouteComponent` geladen und landet dadurch in
 * einem eigenen Buendel (siehe `manualChunks` in `vite.config.ts`). Das ist
 * keine Ladezeitoptimierung: Das Portal darf keinen Agentencode ausliefern.
 * Was nicht im Buendel steht, laesst sich auch nicht aus dem Buendel lesen --
 * und im Agentencode stehen Feldnamen, Endpunkte und Bedienlogik, die einen
 * Portalnutzer nichts angehen.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // TanStack Router signalisiert eine Weiterleitung ueber ein geworfenes
    // Objekt -- das ist die vorgesehene Schnittstelle und kein Fehlerfall.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/app/tickets' });
  },
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  component: () => (
    <AuthGate>
      <AppLayout />
    </AuthGate>
  ),
});

const appIndexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/',
  beforeLoad: () => {
    // TanStack Router signalisiert eine Weiterleitung ueber ein geworfenes
    // Objekt -- das ist die vorgesehene Schnittstelle und kein Fehlerfall.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/app/tickets' });
  },
});

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Die Kundenauswahl des Ticketmoduls in der Adresse -- F1-56.
 *
 * Nur eine Kennung in der richtigen Form wird uebernommen: Etwas anderes
 * beantwortete der Server mit 422, und die Liste zeigte statt der Tickets
 * einen Fehler zu einem Filter, den niemand gesetzt hat. Eine fremde oder
 * geloeschte Kennung dagegen liefert eine leere Liste -- dieselbe Antwort
 * wie ein Kunde ohne Tickets, ohne zu verraten, ob es sie gibt.
 */
function customerSearchParam(value: unknown): string | undefined {
  return typeof value === 'string' && UUID_PATTERN.test(value) ? value : undefined;
}

/**
 * Die Ticketliste liest ihre Einschraenkung aus der Adresse.
 *
 * Bisher stand sie nur im Zustand der Seite. Das kostete drei Dinge, die im
 * Betrieb taeglich gebraucht werden: Ein Filter liess sich nicht als
 * Lesezeichen behalten, nicht an einen Kollegen schicken, und die
 * Zurueck-Taste des Browsers verliess die Liste, statt den vorigen Filter
 * wiederherzustellen. Und es gab keinen Weg, von einer Kennzahl auf der
 * Startseite in genau die Tickets zu springen, die sie zaehlt.
 *
 * Geprueft wird beim Betreten: Was nicht zu den bekannten Zustandsgruppen
 * gehoert, faellt weg. Eine erfundene Gruppe in der Adresse darf keine
 * Abfrage ausloesen, die der Server ohnehin mit 422 beantwortet.
 */
const ticketListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tickets',
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    state?: StateCategory;
    q?: string;
    owner?: 'me';
    unresolved?: true;
    awaiting?: true;
    all?: true;
    sort?: string;
    customer?: string;
  } => {
    const state = STATE_CATEGORIES.find((category) => category === search.state);
    const q = typeof search.q === 'string' && search.q !== '' ? search.q : undefined;
    // "Offen" im Sinne von "noch zu tun" -- alle Zustandsgruppen ausser
    // "Gelöst" und "Geschlossen". Ausdruecklich **nicht** die Gruppe `open`
    // ("In Bearbeitung"): Die laesst ein frisch angelegtes Ticket aus, und
    // genau daran ging die Arbeitsliste der Startseite vorbei (Issue #7).
    //
    // Nur `true` wird uebernommen: `unresolved=false` in der Adresse waere
    // dasselbe wie kein Filter und gehoert dann auch nicht hinein.
    const unresolved =
      search.unresolved === true || search.unresolved === 'true' ? (true as const) : undefined;
    // 'me' und keine Kennung: Eine Nutzerkennung in der Adresse waere ein
    // personenbezogenes Datum im Verlauf des Browsers, im Lesezeichen und in
    // jeder weitergeschickten Verknuepfung -- fuer eine Einschraenkung, die
    // ohnehin nur "ich" bedeutet.
    const owner = search.owner === 'me' ? ('me' as const) : undefined;
    // F1-50: "wartet auf uns". Quer zu den Zustandsreitern, wie ``owner``
    // -- man kann seine offenen Vorgaenge sehen wollen, bei denen jemand
    // wartet. Deshalb ein eigener Schalter und kein Reiter.
    const awaiting =
      search.awaiting === true || search.awaiting === 'true' ? (true as const) : undefined;
    // ``all`` sagt ausdruecklich "alle Zustaende" und ist deshalb noetig
    // (Issue #9): Seit die Liste auf "Offen" beginnt, laesst sich "alles
    // anzeigen" nicht mehr als *Abwesenheit* eines Filters ausdruecken --
    // ohne Angabe waere man wieder bei "Offen", und der Reiter "Alle"
    // schaltete sich beim Anklicken selbst wieder ab.
    const all = search.all === true || search.all === 'true' ? (true as const) : undefined;
    // Die Reihenfolge steht in der Adresse und nicht im Zustand der Seite:
    // Wer eine sortierte Liste weiterschickt, schickt die Sortierung mit.
    // Geprueft wird gegen die Erlaubnisliste -- ein erfundenes Feld darf
    // keine Abfrage ausloesen, die der Server ohnehin mit 422 beantwortet.
    const sort = isSortValue(search.sort) ? search.sort : undefined;
    const customer = customerSearchParam(search.customer);
    return {
      ...(state ? { state } : {}),
      ...(q ? { q } : {}),
      ...(owner ? { owner } : {}),
      ...(unresolved ? { unresolved } : {}),
      ...(awaiting ? { awaiting } : {}),
      ...(all ? { all } : {}),
      ...(sort ? { sort } : {}),
      ...(customer ? { customer } : {}),
    };
  },
  component: TicketListPage,
});

// Die Kanban-Ansicht ist eine zweite Sicht auf dieselbe Liste, kein eigener
// Bestand. Sie bekommt trotzdem eine eigene Adresse: Wer so arbeitet, will
// sie als Lesezeichen behalten.
const boardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/board',
  validateSearch: (search: Record<string, unknown>): { customer?: string } => {
    const customer = customerSearchParam(search.customer);
    return customer ? { customer } : {};
  },
  component: BoardPage,
});

const ticketDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/tickets/$ticketId',
  component: function TicketDetailRoute() {
    const { ticketId } = ticketDetailRoute.useParams();
    return <TicketDetailPage ticketId={ticketId} />;
  },
});

// Das CRM. Die Akte bekommt eine eigene Adresse, damit sie sich verlinken
// laesst -- etwa aus einem Ticket heraus oder in einer Notiz.
const apiTokensRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/api-tokens',
  component: ApiTokensPage,
});

// Die Sicherungsuebersicht -- F5-01. Sie steht **nicht** unter
// /administration: Sie wird gelesen wie eine Ticketliste und nicht
// eingerichtet wie eine Stammdatenliste.
const backupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/backup',
  component: BackupPage,
});

// Die Statusseite -- hinter der Build-Nummer unten in der Navigation.
// Fassung, Serverzustand, Sicherungslaeufe und was zuletzt ausgeliefert
// wurde. Siehe ``features/system/StatusPage.tsx``.
const statusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/status',
  component: StatusPage,
});

// Die Zugaenge zu fremden Diensten -- F5-01. Neben "Zugangstoken" und
// getrennt davon: Die eine Liste vergibt Vollmachten, die andere hinterlegt
// welche. Siehe ``app/navigation.ts``.
const tokensRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/tokens',
  component: TokenManagementPage,
});

// Das Protokoll -- F5-02. Unter /administration, weil es gefuehrt und nicht
// bearbeitet wird, und mit ``privacy.manage`` wie der Auskunftsexport.
const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/audit',
  component: AuditPage,
});

const crmListRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/crm',
  component: CustomerListPage,
});

// Vor der Route mit dem Platzhalter: Andernfalls laese der Router 'import'
// als Kundenkennung.
const crmImportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/crm/import',
  component: ImportPage,
});

const crmFileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/crm/$customerId',
  component: function CustomerFileRoute() {
    const { customerId } = crmFileRoute.useParams();
    return <CustomerFilePage customerId={customerId} />;
  },
});

// Startseite und Berichte -- F5-05, F5-06. Sie werden gelesen und nicht
// eingerichtet und stehen deshalb nicht unter /administration.
const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/reports',
  component: ReportsPage,
});

// Die Zeitauswertung steht **nicht** unter /administration: Sie wird gelesen
// wie eine Ticketliste und nicht eingerichtet wie eine Stammdatenliste.
const timeReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/time-report',
  component: TimeReportPage,
});

// Die eigene Terminuebersicht -- F1-54. Ansicht und Tag stehen in der
// Adresse, damit "Zurueck" im Browser in die vorige Woche fuehrt.
const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/calendar',
  validateSearch: (search: Record<string, unknown>): { view?: 'week' | 'month'; date?: string } => {
    const view = search.view === 'week' || search.view === 'month' ? search.view : undefined;
    const date =
      typeof search.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.date)
        ? search.date
        : undefined;
    return { ...(view ? { view } : {}), ...(date ? { date } : {}) };
  },
  component: function CalendarRoute() {
    const search = calendarRoute.useSearch();
    return <CalendarPage view={search.view ?? 'week'} date={search.date} />;
  },
});

// Die Verwaltung. Eigene Zweige statt einer Seite mit Reitern: Ein
// Administrator soll eine Ansicht verlinken koennen -- etwa den
// Kategoriebaum in einer Arbeitsanweisung.
const masterDataRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/master-data',
  component: MasterDataPage,
});

const categoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/categories',
  component: CategoryPage,
});

const priorityMatrixRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/priority-matrix',
  component: PriorityMatrixPage,
});

// Das Wissen steht **nicht** unter /administration: Es wird gelesen und
// geschrieben wie ein Ticket, nicht eingerichtet wie eine Stammdatenliste.
const knowledgeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/knowledge',
  component: KnowledgePage,
});

/**
 * Die Wissensdatenbank, vorgefiltert auf einen Kunden -- F4-25.
 *
 * Als eigener Pfad und nicht als Abfrageparameter: Der Kunde steht damit in
 * der Adresse, und der Weg zurueck in die Kundenakte ist einer nach oben.
 * Ein Parameter waere kuerzer und im Verlauf des Browsers nicht auffindbar.
 */
const customerKnowledgeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/crm/$customerId/knowledge',
  component: function CustomerKnowledge() {
    const { customerId } = customerKnowledgeRoute.useParams();
    return <KnowledgePage customerId={customerId} />;
  },
});

const brandingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/portal',
  component: BrandingPage,
});

const slaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/sla',
  component: SlaAdminPage,
});

const customFieldsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/custom-fields',
  component: CustomFieldsPage,
});

const snippetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/text-snippets',
  component: SnippetsPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/ticket-templates',
  component: TemplatesPage,
});

const macrosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/macros',
  component: MacrosPage,
});

// Postfaecher, Filter und Signaturen -- F3-01, F3-04, F3-06. Der Kanal, ueber
// den die meisten Tickets hereinkommen, hatte bisher keine Maske.
const mailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/mail',
  component: MailAdminPage,
});

// Die Ankuendigungen -- F3-10. Ein Banner in Bearbeitung und Kundenportal,
// keine Mail; darum eine eigene Seite bei den Kanaelen und kein Reiter der
// E-Mail-Einrichtung.
const announcementsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/announcements',
  component: AnnouncementsPage,
});

// Die Anfrage-Queue -- F5-03. Wie die Sicherungsuebersicht **nicht** unter
// /administration: Nicht zuordenbare Mails zu sichten ist taegliche Arbeit
// und keine Einrichtung -- und sie traegt entsprechend ``mail.triage``, das
// auch die Rolle "Bearbeitung" hat.
const inboundRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/anfragen',
  component: InboundQueuePage,
});

// Die Zugaenge -- F5-16. Bewusst schmal: Sie zeigt, wer da ist, und kann das
// eine, was sonst niemand kann -- den zweiten Faktor zuruecksetzen. Ohne
// diesen Weg waere die Entscheidung gegen Wiederherstellungscodes eine
// Sackgasse gewesen.
const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/users',
  component: UsersPage,
});

// Rollen -- F5-19. Was eine Rolle gewaehrt, stand bisher nur im Code.
const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/roles',
  component: RolesPage,
});

// Checklistenvorlagen -- F1-19. Die Tabelle stand seit Phase 1; es gab nur
// keinen Weg, eine Vorlage anzulegen.
const checklistTemplateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/checklist-templates',
  component: ChecklistTemplatePage,
});

const webFormsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/web-forms',
  component: WebFormsPage,
});

// Die Datenschutzwerkzeuge stehen unter /administration, obwohl sie ein
// eigenes Recht tragen: Wer sie benutzt, richtet nichts ein -- aber er
// arbeitet auch nicht am Ticket. Sie gehoeren zu den Vorgaengen, die der
// Betreiber fuehrt, und nicht zu denen, die der Bearbeiter fuehrt.
const privacyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/privacy',
  component: PrivacyPage,
});

const automationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/automation',
  component: AutomationPage,
});

// Die Webhooks -- F3-18. Eine Schnittstelle und keine Regel; sie stehen
// darum neben den API-Token und nicht als Reiter der Automatisierung.
const webhooksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/administration/webhooks',
  component: WebhooksPage,
});

// Der zweite Faktor -- F5-16. Er steht **nicht** unter /administration: Er
// gehoert zum eigenen Zugang und nicht zur Verwaltung des Mandanten. Jeder
// Angemeldete erreicht ihn, auch wer sonst nichts darf.
// Das Handbuch -- eine eigene Adresse je Kapitel, damit sich ein Kapitel
// verlinken laesst. Genau dafuer ist es da: Auf die Frage "wie geht das
// nochmal" ist der kuerzeste Weg ein Verweis und keine Erklaerung.
//
// Es steht **nicht** unter /administration: Ein Bearbeiter schlaegt darin
// haeufiger nach als ein Administrator.
const handbookRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/handbuch',
  component: HandbookPage,
});

const handbookChapterRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/handbuch/$chapterId',
  component: function HandbookChapterRoute() {
    const { chapterId } = handbookChapterRoute.useParams();
    return <HandbookPage chapterId={chapterId} />;
  },
});

const securityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/security',
  component: SecurityPage,
});

// Die Zufriedenheitsumfrage -- F5-10. Eigene Adresse und **nicht** unter
// /portal: Sie wird ohne Anmeldung ueber ein Merkmal im Link geoeffnet, und
// das Portal verlangt eine Anmeldung. Der Link steht in der Mail nach
// Ticketabschluss (modules/reporting/surveys.py).
//
// Im Portalbuendel, weil sie an jeden ausgeliefert wird, der den Link hat.
const surveyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/umfrage/$token',
  component: lazyRouteComponent(() => import('../features/portal/SurveyPage')),
});

const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal',
  // Eigenes Buendel -- siehe Modulkopf.
  component: lazyRouteComponent(() => import('../features/portal/PortalApp')),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  appRoute.addChildren([
    appIndexRoute,
    ticketListRoute,
    boardRoute,
    ticketDetailRoute,
    apiTokensRoute,
    tokensRoute,
    auditRoute,
    backupRoute,
    statusRoute,
    crmListRoute,
    crmImportRoute,
    crmFileRoute,
    knowledgeRoute,
    customerKnowledgeRoute,
    timeReportRoute,
    calendarRoute,
    dashboardRoute,
    reportsRoute,
    masterDataRoute,
    categoriesRoute,
    priorityMatrixRoute,
    slaRoute,
    brandingRoute,
    customFieldsRoute,
    snippetsRoute,
    templatesRoute,
    macrosRoute,
    automationRoute,
    webhooksRoute,
    webFormsRoute,
    mailRoute,
    announcementsRoute,
    inboundRoute,
    usersRoute,
    rolesRoute,
    checklistTemplateRoute,
    privacyRoute,
    securityRoute,
    handbookRoute,
    handbookChapterRoute,
  ]),
  portalRoute,
  surveyRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: false,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
